import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import InviteUserModal from './InviteUserModal';
import InviteLinkModal from './InviteLinkModal';
import UserDeleteModal from './UserDeleteModal';
import { Users, Plus, Trash2, Shield, Building, UserCheck } from 'lucide-react';

const OrganizationAdminDashboard = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);

  useEffect(() => {
    if (user?.is_organization_admin) {
      loadUsers();
    }
  }, [user]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      
      // Load users from the organization admin's organization only
      const response = await axios.get(`${apiBase}/admin/users`, { headers });
      setUsers(response.data || []);
    } catch (err) {
      setError('Failed to load users: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (inviteData) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      
      // Organization admin can only invite to their own organization
      const response = await axios.post(`${apiBase}/admin/invite-user`, {
        ...inviteData,
        organization_id: user.organization_id  // Force to admin's organization
      }, { headers });
      
      setShowInviteModal(false);
      
      if (response.data.status === 'approved') {
        setInviteResult({
          inviteLink: response.data.invite_link,
          inviteToken: response.data.invite_token,
          email: inviteData.email,
          organizationName: 'your organization',
          role: inviteData.role,
          emailSent: response.data.email_sent || false
        });
        setShowInviteLinkModal(true);
      }
      
      loadUsers(); // Refresh user list
    } catch (err) {
      setError('Failed to invite user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteUser = (userId) => {
    const userToRemove = users.find(u => u.id === userId);
    if (userToRemove) {
      setUserToDelete(userToRemove);
      setShowDeleteModal(true);
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      
      await axios.delete(`${apiBase}/admin/users/${userToDelete.id}`, { headers });
      loadUsers(); // Refresh user list
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (err) {
      setError('Failed to delete user: ' + (err.response?.data?.detail || err.message));
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/users/${userId}/role`, { role: newRole }, { headers });
      loadUsers();
    } catch (err) {
      setError('Failed to update user role: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdateOrgAdmin = async (userId, isOrgAdmin) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/users/${userId}/organization-admin`, 
        { is_organization_admin: isOrgAdmin }, 
        { headers }
      );
      loadUsers();
    } catch (err) {
      setError('Failed to update organization admin status: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (!user?.is_organization_admin) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-6xl mb-4">🚫</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">This area is restricted to organization administrators only.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organization Management</h1>
          <p className="text-gray-600">Manage users for your organization</p>
        </div>
        <div className="flex items-center gap-2">
          <Building className="w-6 h-6 text-blue-600" />
          <span className="text-sm font-medium text-gray-700">{user.organization_id}</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-700">{error}</div>
        </div>
      )}

      {/* Users Management */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Organization Users</h2>
              <p className="text-sm text-gray-600">Invite and manage users in your organization</p>
            </div>
            <button
              onClick={() => setShowInviteModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Invite User
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="text-gray-500">Loading users...</div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No users in your organization</h3>
              <p className="text-gray-600 mb-4">Get started by inviting your first user</p>
              <button
                onClick={() => setShowInviteModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
              >
                Invite User
              </button>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admin Status</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((orgUser) => (
                  <tr key={orgUser.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {orgUser.first_name} {orgUser.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{orgUser.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={orgUser.role}
                        onChange={(e) => handleUpdateUserRole(orgUser.id, e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                        disabled={orgUser.role === 'superadmin'}
                      >
                        <option value="ea">EA</option>
                        <option value="executive">Executive</option>
                        <option value="other">Other</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={orgUser.is_organization_admin || false}
                          onChange={(e) => handleUpdateOrgAdmin(orgUser.id, e.target.checked)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          disabled={orgUser.role === 'superadmin'}
                        />
                        <span className="ml-2 text-sm text-gray-700">Organization Admin</span>
                      </label>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      {orgUser.role !== 'superadmin' && (
                        <button
                          onClick={() => handleDeleteUser(orgUser.id)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Delete User"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      {orgUser.role === 'superadmin' && (
                        <Shield className="w-4 h-4 text-purple-600 mx-auto" title="Superadmin - Cannot modify" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modals */}
      {showInviteModal && (
        <InviteUserModal
          organizations={[{ id: user.organization_id, name: user.organization_id }]}
          onClose={() => setShowInviteModal(false)}
          onInvite={handleInviteUser}
        />
      )}

      {showInviteLinkModal && inviteResult && (
        <InviteLinkModal
          inviteLink={inviteResult.inviteLink}
          email={inviteResult.email}
          organizationName={inviteResult.organizationName}
          role={inviteResult.role}
          emailSentAutomatically={inviteResult.emailSent}
          onClose={() => setShowInviteLinkModal(false)}
        />
      )}

      {showDeleteModal && userToDelete && (
        <UserDeleteModal
          user={userToDelete}
          onConfirm={confirmDeleteUser}
          onCancel={() => {
            setShowDeleteModal(false);
            setUserToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default OrganizationAdminDashboard;